import { useEffect, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Password Cracker (level 5). Compose a guess from the allowed symbols and send it; the
// SERVER grades it and returns per-position feedback:
//   g = right symbol, right slot   y = right symbol, wrong slot   x = not in the password
//
// The password is never in the browser — every guess is a round-trip, and the guess counter
// lives in the server's session state. So this component only renders what it was told.

interface Entry { guess: string; feedback: string }

interface Challenge {
  length: number;
  alphabet: string;
  maxGuesses: number;
  used: number;
  history: Entry[];
}

/** What the engine returns for a graded guess. */
interface ActResult {
  length: number;
  alphabet: string;
  maxGuesses: number;
  used: number;
  history: Entry[];
  solved: boolean;
  finished: boolean;
}

const CELL: Record<string, { bg: string; border: string; color: string }> = {
  g: { bg: '#14532d', border: '#4ade80', color: '#86efac' },
  y: { bg: '#3f3f11', border: '#facc15', color: '#fde047' },
  x: { bg: '#1f2430', border: '#4b5563', color: '#9ca3af' },
};

export default function PasswordCrackerView({ challenge, onAct, busy }: MinigameViewProps<Challenge, ActResult>) {
  const length = challenge?.length ?? 4;
  const alphabet = challenge?.alphabet ?? '0123456789ABCDEF';
  const maxGuesses = challenge?.maxGuesses ?? 8;

  const [history, setHistory] = useState<Entry[]>(challenge?.history ?? []);
  const [draft, setDraft] = useState<string[]>(() => Array(length).fill(''));

  // A fresh challenge (e.g. "Play again") resets the board.
  useEffect(() => {
    setHistory(challenge?.history ?? []);
    setDraft(Array(challenge?.length ?? 4).fill(''));
  }, [challenge]);

  const full = draft.every(c => c !== '');
  const guessesLeft = maxGuesses - history.length;

  const push = (symbol: string) => {
    const i = draft.findIndex(c => c === '');
    if (i === -1) return;
    const next = [...draft];
    next[i] = symbol;
    setDraft(next);
  };

  const back = () => {
    const filled = draft.map(c => c !== '').lastIndexOf(true);
    if (filled === -1) return;
    const next = [...draft];
    next[filled] = '';
    setDraft(next);
  };

  const send = async () => {
    if (!full || busy) return;
    const res = await onAct({ guess: draft.join('') });
    // null = rejected, or the attempt ended (the host page shows the result).
    if (res) {
      setHistory(res.history ?? []);
      setDraft(Array(length).fill(''));
    }
  };

  // Physical keyboard: type symbols, Backspace to correct, Enter to send.
  // Re-registered every render on purpose, so the handler always closes over the
  // current draft/history instead of a stale snapshot.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy) return;
      const k = e.key.toUpperCase();
      if (k === 'ENTER') { void send(); return; }
      if (k === 'BACKSPACE') { e.preventDefault(); back(); return; }
      if (k.length === 1 && alphabet.includes(k)) push(k);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Symbols already proven absent — struck through, so deduction is readable at a glance.
  const ruledOut = new Set<string>();
  for (const h of history) {
    for (let i = 0; i < h.guess.length; i++) {
      if (h.feedback[i] === 'x') ruledOut.add(h.guess[i]);
    }
  }
  // A symbol seen as g/y anywhere is definitely in the password.
  const confirmed = new Set<string>();
  for (const h of history) {
    for (let i = 0; i < h.guess.length; i++) {
      if (h.feedback[i] !== 'x') confirmed.add(h.guess[i]);
    }
  }

  const cellBox = (fb?: string): React.CSSProperties => {
    const s = fb ? CELL[fb] ?? CELL.x : { bg: '#0f1218', border: '#2a2a2a', color: '#e2e8f0' };
    return {
      width: '2.2rem', height: '2.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: 4, fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 'bold',
    };
  };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
        Crack a <strong>{length}-symbol</strong> password (no repeats) in <strong>{maxGuesses}</strong> guesses.{' '}
        <span style={{ color: '#86efac' }}>green</span> = right symbol in the right slot ·{' '}
        <span style={{ color: '#fde047' }}>yellow</span> = right symbol, wrong slot ·{' '}
        <span style={{ color: '#9ca3af' }}>grey</span> = not in the password.
      </p>

      {/* Guess history */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' }}>
        {history.map((h, r) => (
          <div key={r} className="fx-fade-in" style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
            <span style={{ color: '#4b5563', fontSize: '0.7rem', width: '1.3rem', textAlign: 'right' }}>{r + 1}</span>
            {h.guess.split('').map((ch, i) => (
              <div key={i} style={cellBox(h.feedback[i])}>{ch}</div>
            ))}
          </div>
        ))}
        {history.length === 0 && (
          <span style={{ color: '#4b5563', fontSize: '0.78rem' }}>No attempts yet — send your first guess.</span>
        )}
      </div>

      {/* Draft row */}
      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', marginBottom: '0.6rem' }}>
        <span style={{ color: 'var(--primary-color)', fontSize: '0.7rem', width: '1.3rem', textAlign: 'right' }}>&gt;</span>
        {draft.map((ch, i) => (
          <div key={i} style={cellBox()}>{ch || <span style={{ color: '#2a2a2a' }}>·</span>}</div>
        ))}
        <span style={{ marginLeft: '0.5rem', color: guessesLeft <= 2 ? '#f87171' : 'var(--text-muted)', fontSize: '0.75rem' }}>
          {guessesLeft} guess{guessesLeft === 1 ? '' : 'es'} left
        </span>
      </div>

      {/* Keypad */}
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
        {alphabet.split('').map(sym => {
          const out = ruledOut.has(sym);
          const inPwd = confirmed.has(sym);
          return (
            <button
              key={sym}
              onClick={() => push(sym)}
              disabled={busy}
              title={out ? 'Ruled out' : inPwd ? 'Confirmed in the password' : undefined}
              style={{
                width: '2.1rem', height: '2.1rem', padding: 0,
                fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 'bold',
                background: out ? '#15181d' : inPwd ? '#14532d' : 'transparent',
                border: `1px solid ${out ? '#3a4250' : inPwd ? '#4ade80' : 'var(--primary-color)'}`,
                color: out ? '#6b7280' : inPwd ? '#86efac' : 'var(--primary-color)',
                textDecoration: out ? 'line-through' : undefined,
                borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {sym}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="primary" onClick={send} disabled={!full || busy} style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}>
          {busy ? '…' : 'Send guess'}
        </button>
        <button onClick={back} disabled={busy} style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}>
          Backspace
        </button>
        <span style={{ color: '#4b5563', fontSize: '0.72rem' }}>
          Keyboard works too: type symbols, Enter to send.
        </span>
      </div>
    </div>
  );
}
